import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { admin } from "../lib/api";
import { useCurrency } from "../lib/auth";
import { money } from "../lib/format";
import type { List, ProductSummary } from "../lib/types";
import { Badge, Button, PageHeader, Spinner, Table } from "../components/ui";
import { thumb } from "../components/ImageManager";

const STATUS = { active: { label: "Activ", tone: "green" }, draft: { label: "Ciornă", tone: "gray" }, archived: { label: "Arhivat", tone: "amber" } } as const;

export default function Products() {
  const nav = useNavigate();
  const currency = useCurrency();
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["products", { term, status }],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: "50" });
      if (term) p.set("q", term);
      if (status) p.set("status", status);
      if (pageParam) p.set("cursor", pageParam);
      return admin.get<List<ProductSummary>>(`/products?${p}`);
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
  });
  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <>
      <PageHeader title="Produse" actions={<Button onClick={() => nav("/products/new")}>+ Adaugă produs</Button>} />
      <form className="mb-4 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
        <input className="input max-w-xs" placeholder="Caută după nume sau SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Toate statusurile</option>
          <option value="active">Active</option>
          <option value="draft">Ciorne</option>
          <option value="archived">Arhivate</option>
        </select>
        <Button variant="secondary">Caută</Button>
      </form>
      {query.isLoading ? <Spinner /> : (
        <Table head={["", "Produs", "Status", "Stoc", "Preț", "Variante"]} empty={rows.length === 0}>
          {rows.map((p) => (
            <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/products/${p.id}`)}>
              <td className="w-14 px-4 py-2">
                <div className="h-10 w-10 overflow-hidden rounded-md bg-slate-100">{p.image && <img src={thumb(p.image)} alt="" className="h-full w-full object-cover" />}</div>
              </td>
              <td className="px-4 py-2">
                <Link to={`/products/${p.id}`} className="font-semibold hover:text-brand">{p.title}</Link>
                <div className="text-xs text-slate-400">{p.vendor}</div>
              </td>
              <td className="px-4 py-2"><Badge tone={STATUS[p.status as keyof typeof STATUS]?.tone}>{STATUS[p.status as keyof typeof STATUS]?.label ?? p.status}</Badge></td>
              <td className={`px-4 py-2 ${p.available === 0 ? "font-semibold text-red-600" : ""}`}>{p.available} buc.</td>
              <td className="px-4 py-2">{p.price_min === p.price_max ? money(p.price_min, currency) : `${money(p.price_min, currency)} – ${money(p.price_max, currency)}`}</td>
              <td className="px-4 py-2 text-slate-500">{p.variants_count}</td>
            </tr>
          ))}
        </Table>
      )}
      {query.hasNextPage && (
        <div className="mt-4 text-center">
          <Button variant="secondary" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>Încarcă mai multe</Button>
        </div>
      )}
    </>
  );
}
